import React from 'react';
import { Form, Row, Col, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useFileUpload from 'hooks/useFileUpload';
import FileUploader from 'components/common/FileUploader';
import useFormValidation from 'hooks/useFormValidation';

import { COUNTRY_LIST, STATE_LIST } from 'constants/address';
import { WAREHOUSE_VALIDATION_SCHEMA } from 'constants/admin/warehouse';

const WarehouseEditForm = ({ warehouse, onSubmit, onCancel, isLoading }) => {
  const initialValues = {
    name: warehouse?.name || '',
    party_name: warehouse?.party_name || '',
    inventory_available: warehouse?.inventory_available,
    is_awb_auto_generate: warehouse?.is_awb_auto_generate ?? false,
    is_expire_date_required: warehouse?.is_expire_date_required ?? false,
    is_picklist_pass_enabled: warehouse?.is_picklist_pass_enabled ?? false,
    is_picking_video_enabled: warehouse?.is_picking_video_enabled ?? false,
    address_name: warehouse?.address_name || '',
    address_line_1: warehouse?.address_line_1 || '',
    address_line_2: warehouse?.address_line_2 || '',
    address_city: warehouse?.address_city || '',
    address_state: warehouse?.address_state || '',
    address_country: warehouse?.address_country || '',
    address_pincode: warehouse?.address_pincode || '',
    address_email: warehouse?.address_email || '',
    address_phone: warehouse?.address_phone || '',
    gstin: warehouse?.gstin || '',
  };

  const logoUpload = useFileUpload({
    fieldId: 'logo_image',
    initialFile: warehouse?.logo_image,
  });

  const signatureUpload = useFileUpload({
    fieldId: 'signature_image',
    initialFile: warehouse?.signature_image,
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    isValid,
  } = useFormValidation(initialValues, WAREHOUSE_VALIDATION_SCHEMA);

  const handleSubmit = e => {
    e.preventDefault();

    const imageStates = {
      logo_image: {
        file: logoUpload.file,
        isRemoved: logoUpload.isRemoved,
        hasChanged: logoUpload.hasChanged,
      },
      signature_image: {
        file: signatureUpload.file,
        isRemoved: signatureUpload.isRemoved,
        hasChanged: signatureUpload.hasChanged,
      },
    };

    onSubmit(e, imageStates);
  };

  const handleCancel = () => {
    logoUpload.clearState();
    signatureUpload.clearState();

    onCancel();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={values.name}
              onChange={e => handleChange('name', e.target.value)}
              onBlur={() => handleBlur('name')}
              isInvalid={touched.name && errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Party Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="party_name"
              value={values.party_name}
              onChange={e => handleChange('party_name', e.target.value)}
              onBlur={() => handleBlur('party_name')}
              isInvalid={touched.party_name && errors.party_name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.party_name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Status <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="inventory_available"
              value={values.inventory_available}
              onChange={e =>
                handleChange('inventory_available', e.target.value)
              }
              onBlur={() => handleBlur('inventory_available')}
              isInvalid={
                touched.inventory_available && errors.inventory_available
              }
            >
              <option value="true">Available</option>
              <option value="false">Unavailable</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.inventory_available}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <input
              type="hidden"
              name="is_awb_auto_generate"
              value={values.is_awb_auto_generate ? 'true' : 'false'}
            />
            <Form.Check
              type="switch"
              id="is_awb_auto_generate"
              label="AWB Auto Generate"
              checked={values.is_awb_auto_generate}
              onChange={e =>
                handleChange('is_awb_auto_generate', e.target.checked)
              }
            />
            <Form.Text className="text-danger">
              * Deactivate this feature when you want to defer AWB issuance.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <input
              type="hidden"
              name="is_expire_date_required"
              value={values.is_expire_date_required ? 'true' : 'false'}
            />
            <Form.Check
              type="switch"
              id="is_expire_date_required"
              label="Expire Date Required"
              checked={values.is_expire_date_required}
              onChange={e =>
                handleChange('is_expire_date_required', e.target.checked)
              }
            />
            <Form.Text className="text-muted">
              * When enabled, expire date is required for inventory creation and
              GRN.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <input
              type="hidden"
              name="is_picklist_pass_enabled"
              value={values.is_picklist_pass_enabled ? 'true' : 'false'}
            />
            <Form.Check
              type="switch"
              id="is_picklist_pass_enabled"
              label="Picklist Pass (Direct AWB)"
              checked={values.is_picklist_pass_enabled}
              onChange={e =>
                handleChange('is_picklist_pass_enabled', e.target.checked)
              }
            />
            <Form.Text className="text-muted">
              * When enabled, shipments can skip picklist and directly generate
              AWB.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="is_picking_video_enabled"
              label="Picking Video Recording"
              checked={values.is_picking_video_enabled}
              disabled
            />
            <Form.Text className="text-muted">
              * When enabled, webcam video is recorded during picking and
              uploaded automatically. To change this setting, please contact the
              WMS operations team.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Logo Image</Form.Label>
            <FileUploader
              file={logoUpload.file}
              initialFile={logoUpload.initialFile}
              getRootProps={logoUpload.getRootProps}
              getInputProps={logoUpload.getInputProps}
              resetFile={logoUpload.resetFile}
              isRemoved={logoUpload.isRemoved}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Signature Image</Form.Label>
            <FileUploader
              file={signatureUpload.file}
              initialFile={signatureUpload.initialFile}
              getRootProps={signatureUpload.getRootProps}
              getInputProps={signatureUpload.getInputProps}
              resetFile={signatureUpload.resetFile}
              isRemoved={signatureUpload.isRemoved}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Alert variant="warning" className="d-flex align-items-center mb-3">
            <FontAwesomeIcon
              icon="exclamation-triangle"
              className="me-2 flex-shrink-0"
            />
            <span>
              All address fields below are{' '}
              <strong>required for AWB issuance</strong>. AWB cannot be
              generated if any of these fields are missing.
            </span>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>
              Authorised Person <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="address_name"
              value={values.address_name}
              onChange={e => handleChange('address_name', e.target.value)}
              onBlur={() => handleBlur('address_name')}
              isInvalid={touched.address_name && errors.address_name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address_name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Address Line 1 <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="address_line_1"
              value={values.address_line_1}
              onChange={e => handleChange('address_line_1', e.target.value)}
              onBlur={() => handleBlur('address_line_1')}
              isInvalid={touched.address_line_1 && errors.address_line_1}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address_line_1}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Address Line 2 <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="address_line_2"
              value={values.address_line_2}
              onChange={e => handleChange('address_line_2', e.target.value)}
              onBlur={() => handleBlur('address_line_2')}
              isInvalid={touched.address_line_2 && errors.address_line_2}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address_line_2}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              City <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="address_city"
              value={values.address_city}
              onChange={e => handleChange('address_city', e.target.value)}
              onBlur={() => handleBlur('address_city')}
              isInvalid={touched.address_city && errors.address_city}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address_city}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              State <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="address_state"
              value={values.address_state}
              onChange={e => handleChange('address_state', e.target.value)}
              onBlur={() => handleBlur('address_state')}
              isInvalid={touched.address_state && errors.address_state}
            >
              <option value="">Select State...</option>
              {STATE_LIST.map(state => (
                <option key={state.value} value={state.value}>
                  {state.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.address_state}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Country <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="address_country"
              value={values.address_country}
              onChange={e => handleChange('address_country', e.target.value)}
              onBlur={() => handleBlur('address_country')}
              isInvalid={touched.address_country && errors.address_country}
            >
              <option value="">Select Country...</option>
              {COUNTRY_LIST.map(country => (
                <option key={country.value} value={country.value}>
                  {country.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.address_country}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Pincode <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="address_pincode"
              value={values.address_pincode}
              onChange={e => handleChange('address_pincode', e.target.value)}
              onBlur={() => handleBlur('address_pincode')}
              isInvalid={touched.address_pincode && errors.address_pincode}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address_pincode}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Email <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              name="address_email"
              value={values.address_email}
              onChange={e => handleChange('address_email', e.target.value)}
              onBlur={() => handleBlur('address_email')}
              isInvalid={touched.address_email && errors.address_email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address_email}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Phone <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="address_phone"
              value={values.address_phone}
              onChange={e => handleChange('address_phone', e.target.value)}
              onBlur={() => handleBlur('address_phone')}
              isInvalid={touched.address_phone && errors.address_phone}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address_phone}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              GSTIN <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="gstin"
              value={values.gstin}
              onChange={e => handleChange('gstin', e.target.value)}
              onBlur={() => handleBlur('gstin')}
              isInvalid={touched.gstin && errors.gstin}
            />
            <Form.Control.Feedback type="invalid">
              {errors.gstin}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="success"
          disabled={!isValid || isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </Form>
  );
};

export default WarehouseEditForm;
